import { isEqual } from 'lodash'
import {
  FILTER_CONFIG,
  FilterTypes,
  getFilterDefault,
} from '@/shared/constants/booking/filterConfig'

/**
 * Generates the default state from filter configuration.
 * @returns {Object} The default state object
 */
const generateState = () => {
  const state = {}
  Object.values(FILTER_CONFIG).forEach((config) => {
    const foundKey = Object.keys(FILTER_CONFIG).find(
      (key) => FILTER_CONFIG[key].stateKey === config.stateKey
    )
    state[config.stateKey] = getFilterDefault(foundKey)
  })
  return state
}

/**
 * Capitalizes the first letter of a string.
 * @param {string} str - The string to capitalize
 * @returns {string} The capitalized string
 */
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * Generates getters from filter configuration.
 * - For OBJECT types with subKeys: creates sub-getters using getterPrefix
 *   - If getterPrefix is empty: uses subKey directly (e.g., 'startDate')
 *   - If getterPrefix is set: uses prefix + SubKey (e.g., 'dateFilterStartDate')
 * - For ARRAY/STRING types: creates direct getters using stateKey
 * @returns {Object} The getters object
 */
const generateGetters = () => {
  const getters = {}

  Object.entries(FILTER_CONFIG).forEach(([key, config]) => {
    if (config.type === FilterTypes.OBJECT && config.subKeys) {
      // Use getterPrefix if defined, otherwise fall back to key
      const prefix = config.getterPrefix ?? key
      config.subKeys.forEach((subKey) => {
        // If prefix is empty, use subKey directly; otherwise prefix + SubKey
        const getterName = prefix ? `${prefix}${capitalize(subKey)}` : subKey
        getters[getterName] = (state) => state[config.stateKey][subKey]
      })
    } else {
      // Direct getter using stateKey as the getter name
      getters[config.stateKey] = (state) => state[config.stateKey]
    }
  })

  return getters
}

/**
 * Generates mutations from filter configuration.
 * - For OBJECT types: creates partial-update mutations
 * - For ARRAY/STRING types: creates direct-set mutations
 * @returns {Object} The mutations object
 */
const generateMutations = () => {
  const mutations = {}

  Object.entries(FILTER_CONFIG).forEach(([key, config]) => {
    // Convert key to mutation name: statuses -> SET_STATUSES, dateFilter -> SET_DATE_FILTER
    const mutationName = `SET_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`

    if (config.type === FilterTypes.OBJECT) {
      // Partial update mutation for composite filters
      mutations[mutationName] = (state, payload) => {
        Object.keys(payload).forEach((k) => {
          if (payload[k] !== undefined) {
            state[config.stateKey][k] = payload[k]
          }
        })
      }
    } else {
      // Direct set mutation for simple filters
      mutations[mutationName] = (state, value) => {
        state[config.stateKey] = value
      }
    }
  })

  return mutations
}

/**
 * Pre-computed defaults for filters checked by isAnyFilterActive.
 * Excludes dateFilter (dynamic default) and searchQuery.
 * Computed once at module load time for performance.
 */
const activeFilterDefaults = Object.entries(FILTER_CONFIG)
  .filter(([key]) => key !== 'dateFilter' && key !== 'searchQuery')
  .reduce((acc, [key, config]) => {
    acc[config.stateKey] = getFilterDefault(key)
    return acc
  }, {})

/**
 * Checks if any filter (except date and search) is currently active.
 * Compares current state against pre-computed defaults.
 * @param {Object} state - The store state
 * @returns {boolean} True if any filter is active
 */
const isAnyFilterActive = (state) => {
  return Object.entries(activeFilterDefaults).some(
    ([stateKey, defaultValue]) => !isEqual(state[stateKey], defaultValue)
  )
}

/**
 * Returns the number of active filters (except date and search).
 * @param {Object} state - The store state
 * @returns {number} The number of active filters
 */
const activeFilterCount = (state) => {
  return Object.entries(activeFilterDefaults).reduce(
    (count, [stateKey, defaultValue]) => {
      const currentValue = state[stateKey]

      // If it's an array filter, add the length
      if (Array.isArray(currentValue) && currentValue.length > 0) {
        return count + currentValue.length
      }

      // If it's an object filter (like timeFilter), add 1 if different from default
      if (typeof currentValue === 'object' && currentValue !== null) {
        return count + (!isEqual(currentValue, defaultValue) ? 1 : 0)
      }

      // For other types, add 1 if different from default
      return count + (!isEqual(currentValue, defaultValue) ? 1 : 0)
    },
    0
  )
}

// Generate state, getters, and mutations from config
const state = generateState()
const generatedGetters = generateGetters()
const generatedMutations = generateMutations()

export default {
  namespaced: true,
  state,
  getters: {
    ...generatedGetters,

    /**
     * Returns true if any filter has been applied (except date and search)
     */
    isAnyFilterActive,

    /**
     * Returns the total count of active filter selections
     */
    activeFilterCount,
  },
  mutations: {
    ...generatedMutations,

    /**
     * Resets selection filters to defaults (user action).
     * Only resets filters tracked by isAnyFilterActive.
     * Does NOT reset dateFilter or searchQuery.
     */
    RESET_ALL_FILTERS(state) {
      Object.entries(activeFilterDefaults).forEach(
        ([stateKey, defaultValue]) => {
          state[stateKey] = Array.isArray(defaultValue)
            ? [...defaultValue]
            : typeof defaultValue === 'object' && defaultValue !== null
            ? { ...defaultValue }
            : defaultValue
        }
      )
    },

    /**
     * Resets store to default state (system-level cleanup)
     */
    CLEAR_STORE(state) {
      Object.assign(state, generateState())
    },
  },
  actions: {
    /**
     * Resets store to default state
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
